import { ref, shallowRef, watch } from "vue";
import { toValue } from '../assets/js/vue/utlis/toValue';

/**
 * @typedef AsyncDataResult
 * @type {object}
 * @property {import('vue').Ref<any>} data результата вызова
 * @property {import('vue').Ref<any>} pending статус вызова
 * @property {import('vue').Ref<any>} error объект с ошибкой
 * @property {() => Promise<void>} execute повторный вызов
 */

/**
 * @TODO добавить кэширование
 *
 * Бойлерплейт для вызова асинхронной функции (cb), возвращает реактивный объект с данными результата вызова.
 *
 * @param {(signal: AbortSignal) => void} cb функция для асинхронного вызова
 * @param {object} options
 * @param {import('vue').Ref<any>[]|import('vue').Ref<any>=} options.watch Можно указать реактивные переменные, при изменении которых функция вызывается повторно.
 * @param {boolean} [options.immediate=false] вызвать функцию немедленно
 * @param {boolean} [options.deep=false] возвращаемые данные (data) могут быть глубоко реактивными или нет
 * @param {'cancel'|'defer'} [options.dedupe='cancel']  'defer' возвращает первый промис (при повторном/одновременном) вызове (execute()), 'cancel' - отменяет текущий невыполненный промис и возвращает последний
 * @param {import('vue').Ref<any>} [options.default=null] значение по умолчанию (реактивная переменная или функция)
 * @returns {Promise<AsyncDataResult> & AsyncDataResult}
 */
export const useAsyncData = (cb, options = {}) => {
    const defaultValue = () => options?.default ? toValue(options?.default) : null;
    const immediate = options?.immediate ?? false;
    const deep = options?.deep ?? false;
    const dedupe = options?.dedupe ?? 'cancel' // 'cancel' | 'defer', where 'defer' - rerurns first promise, 'cancel' - cancelled current promise, and returns last promise

    let promiseLocal;
    let controller;

    const asyncData = {
        pending: ref(false),
        error: shallowRef(null),
        data: deep ? ref(defaultValue()) : shallowRef(defaultValue()),
    };

    asyncData.execute = () => {
        if (promiseLocal) {
            if (dedupe === 'defer')
                return promiseLocal;

            // abort prev promise
            if (controller) {
                controller.abort('cancelled');
                // create new controller
                controller = new AbortController();
            }
        }

        if (!controller) {
            controller = new AbortController();
        }

        asyncData.pending.value = true;

        promiseLocal = new Promise((res, rej) => {
            // sign promise for new controller
            controller.signal.addEventListener('abort', () => {
                rej(controller.signal.reason);
            });

            Promise.resolve(cb({ signal: controller.signal }))
                .then((result) => res(result))
                .catch((err) => rej(err));
        })
            .then((res) => {
                asyncData.data.value = res;
                asyncData.error.value = undefined;
                asyncData.pending.value = false;

                controller = undefined;
                promiseLocal = undefined;
            })
            .catch((err) => {
                if (err === 'cancelled') {
                    return promiseLocal;
                }

                asyncData.data.value = defaultValue();
                asyncData.error.value = err;
                asyncData.pending.value = false;

                controller = undefined;
                promiseLocal = undefined;
            });

        return promiseLocal;
    };

    if (options?.watch) watch(options?.watch, asyncData.execute);
    if (immediate) asyncData.execute();

    const asyncDataPromise = Promise.resolve(promiseLocal).then(() => asyncData);
    Object.assign(asyncDataPromise, asyncData)

    return asyncDataPromise;
};
